/**
 * Component: Widget Surface Mount Helpers
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R09, WIDGET-V1-R10, WIDGET-V1-R11, WIDGET-V1-R12,
 *               WIDGET-V1-R13
 *
 * The widget ships two integration paths (NPM + UMD
 * auto-init). Both need a uniform way to mount a chosen
 * surface into the ``WidgetRuntime.container`` returned by
 * the init pipeline.
 *
 * ``mountSurface`` creates a React root inside the container
 * and renders ``SurfaceHost`` — a thin wrapper that renders
 * the chosen surface and, on a cross-module ``BrowseLink``
 * click, swaps to the Browse viewer *in place* (keeping the
 * original surface mounted-but-hidden so its state survives,
 * and pushing a browser history entry so Back returns to it).
 * It returns a handle for unmount / re-mount — the auto-init
 * path uses it to tear the widget down on hot-reloads; the
 * NPM path uses it to re-render after a token refresh.
 *
 * Keeping this glue separate from the React components lets
 * the surface components stay pure (no side effects at module
 * load; no hard-coded DOM operations) so they remain
 * unit-testable via React Testing Library.
 */

import React from 'react';
import { createRoot, type Root } from 'react-dom/client';

import type { WidgetRuntime } from '../core/init';
import { assertSurfaceEnabled } from '../core/surfaces';
import type { WidgetSurface } from '../core/config';

import { SurfaceHost } from './surface-host';

/**
 * Handle returned by ``mountSurface``. ``unmount`` tears
 * the React tree down and empties the container so the
 * host page can reuse the element for something else.
 */
export interface WidgetSurfaceHandle {
  unmount: () => void;
}

/**
 * Mount a surface into the widget runtime's container.
 *
 * Runs the R15 enabled-surfaces gate a second time here
 * (even though the init pipeline already did) so a caller
 * that creates a runtime but then mounts a different surface
 * than the one init validated still fails cleanly — throws
 * ``WidgetSurfaceDisabledError`` before touching the DOM.
 */
export function mountSurface(
  runtime: WidgetRuntime,
  surface: string,
  options: { onError?: (error: Error) => void } = {},
): WidgetSurfaceHandle {
  assertSurfaceEnabled(
    surface,
    runtime.config.config.enabled_surfaces as readonly WidgetSurface[],
  );

  const root = createRoot(runtime.container);
  root.render(
    React.createElement(SurfaceHost, {
      runtime,
      surface,
      onError: options.onError,
    }),
  );

  return {
    unmount: () => {
      rootUnmount(root);
    },
  };
}

function rootUnmount(root: Root): void {
  try {
    root.unmount();
  } catch {
    // Unmount is best-effort; React throws if the root
    // has already been torn down, which we treat as a
    // no-op rather than propagating.
  }
}
